import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import RetroTypeModal, { RETRO_TYPES, RetroTypeKey } from '../components/RetroTypeModal';
import { useAuth } from '../context/AuthContext';
import * as retroService from '../services/retrospectiveService';
import type { Retrospective, RetrospectiveFull, RetrospectiveActionItem } from '../services/retrospectiveService';
import * as teamService from '../services/teamService';
import type { Team, TeamMember } from '../services/teamService';
import { getSprints } from '../services/taskboardService';

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateISO(date: Date) {
  return date.toISOString().split('T')[0];
}

const RETRO_TYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  four_ls: "4 L's",
  start_stop_continue: 'Start Stop Continue',
  mad_sad_glad: 'Mad Sad Glad',
  sailboat: 'Sailboat',
  starfish: 'Starfish',
};

interface SectionItems {
  [sectionKey: string]: { id?: string; content: string; sort_order?: number }[];
}

interface ActionItemDraft {
  id?: string;
  content: string;
  assignee_id: string | null;
  sort_order?: number;
}

export default function Retrospectives(): React.ReactElement {
  const { user, token } = useAuth();

  // List state
  const [retros, setRetros] = useState<Retrospective[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedRetro, setSelectedRetro] = useState<RetrospectiveFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filter
  const [filterTeamId, setFilterTeamId] = useState<string>('');

  // Teams & sprints (shared by filter & form)
  const [teams, setTeams] = useState<Team[]>([]);
  const [sprints, setSprints] = useState<any[]>([]);

  // Type selection modal
  const [showTypeModal, setShowTypeModal] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingRetro, setEditingRetro] = useState<RetrospectiveFull | null>(null);
  const [formType, setFormType] = useState<RetroTypeKey>('standard');
  const [formTitle, setFormTitle] = useState('');
  const [formTeamId, setFormTeamId] = useState('');
  const [formSprintId, setFormSprintId] = useState('');
  const [formDate, setFormDate] = useState(formatDateISO(new Date()));
  const [formStatus, setFormStatus] = useState('draft');
  const [formSections, setFormSections] = useState<SectionItems>({});
  const [formAttendeeIds, setFormAttendeeIds] = useState<string[]>([]);
  const [formActionItems, setFormActionItems] = useState<ActionItemDraft[]>([]);
  const [saving, setSaving] = useState(false);

  // Team members (for attendees)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Carried-forward action items
  const [carriedForwardItems, setCarriedForwardItems] = useState<RetrospectiveActionItem[]>([]);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Timer
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timerMinutes, setTimerMinutes] = useState(5);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRemaining, setTimerRemaining] = useState<number | null>(null); // seconds remaining
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Timer logic
  useEffect(() => {
    if (timerRunning && timerRemaining !== null && timerRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimerRemaining(prev => {
          if (prev === null || prev <= 1) {
            setTimerRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerRunning]);

  function startTimer() {
    const totalSeconds = timerMinutes * 60 + timerSeconds;
    if (totalSeconds <= 0) return;
    setTimerRemaining(totalSeconds);
    setTimerRunning(true);
    setShowTimerModal(false);
  }

  function stopTimer() {
    setTimerRunning(false);
  }

  function resetTimer() {
    setTimerRunning(false);
    setTimerRemaining(null);
  }

  function formatTimer(secs: number): string {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  // Load teams on mount
  useEffect(() => {
    if (!token) return;
    teamService.listTeams(token).then(setTeams).catch(console.error);
    getSprints('', token).then(res => setSprints(res.data || [])).catch(console.error);
  }, [token]);

  // Load retros
  const loadRetros = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await retroService.listRetrospectives(token, filterTeamId || undefined);
      setRetros(data);
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
      } else if (data.length === 0) {
        setSelectedId(null);
        setSelectedRetro(null);
      }
    } catch (err) {
      console.error('Failed to load retrospectives:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filterTeamId, selectedId]);

  useEffect(() => {
    loadRetros();
  }, [token, filterTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load selected retro detail
  useEffect(() => {
    if (!token || !selectedId) {
      setSelectedRetro(null);
      return;
    }
    setDetailLoading(true);
    retroService.getRetrospective(selectedId, token)
      .then(setSelectedRetro)
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, [token, selectedId]);

  // Load team members when form team changes
  useEffect(() => {
    if (!token || !formTeamId) {
      setTeamMembers([]);
      return;
    }
    teamService.getTeamById(formTeamId, token)
      .then(t => setTeamMembers(t.members))
      .catch(console.error);
  }, [token, formTeamId]);

  // Load carried-forward action items when form team changes
  useEffect(() => {
    if (!token || !formTeamId) {
      setCarriedForwardItems([]);
      return;
    }
    retroService.getOpenActionItems(formTeamId, token)
      .then(items => {
        // Exclude items that belong to the retro being edited
        if (editingRetro) {
          setCarriedForwardItems(items.filter(i => i.retrospective_id !== editingRetro.id));
        } else {
          setCarriedForwardItems(items);
        }
      })
      .catch(console.error);
  }, [token, formTeamId, editingRetro]);

  function handleSelectType(type: RetroTypeKey) {
    setShowTypeModal(false);
    setEditingRetro(null);
    setFormType(type);
    setFormTitle('');
    setFormTeamId('');
    setFormSprintId('');
    setFormDate(formatDateISO(new Date()));
    setFormStatus('draft');
    setFormAttendeeIds([]);
    setFormActionItems([]);

    // Initialize sections
    const sections: SectionItems = {};
    RETRO_TYPES[type].sections.forEach(s => {
      sections[s.key] = [];
    });
    setFormSections(sections);

    setShowForm(true);
  }

  function openEditForm(retro: RetrospectiveFull) {
    setEditingRetro(retro);
    setFormType(retro.retro_type as RetroTypeKey);
    setFormTitle(retro.title || '');
    setFormTeamId(retro.team_id || '');
    setFormSprintId(retro.sprint_id || '');
    setFormDate(retro.retro_date ? retro.retro_date.split('T')[0] : formatDateISO(new Date()));
    setFormStatus(retro.status);

    // Populate sections from items
    const sections: SectionItems = {};
    const typeConfig = RETRO_TYPES[retro.retro_type as RetroTypeKey];
    if (typeConfig) {
      typeConfig.sections.forEach(s => {
        sections[s.key] = retro.items
          .filter(i => i.section_key === s.key)
          .map(i => ({ id: i.id, content: i.content, sort_order: i.sort_order }));
      });
    }
    setFormSections(sections);

    setFormAttendeeIds(retro.attendees.map(a => a.user_id));
    setFormActionItems(retro.action_items.map(ai => ({
      id: ai.id,
      content: ai.content,
      assignee_id: ai.assignee_id,
      sort_order: ai.sort_order,
    })));

    setShowForm(true);
  }

  function addItemToSection(sectionKey: string) {
    setFormSections(prev => ({
      ...prev,
      [sectionKey]: [...(prev[sectionKey] || []), { content: '' }],
    }));
  }

  function updateSectionItem(sectionKey: string, index: number, content: string) {
    setFormSections(prev => ({
      ...prev,
      [sectionKey]: prev[sectionKey].map((item, i) => i === index ? { ...item, content } : item),
    }));
  }

  function removeSectionItem(sectionKey: string, index: number) {
    setFormSections(prev => ({
      ...prev,
      [sectionKey]: prev[sectionKey].filter((_, i) => i !== index),
    }));
  }

  function addActionItem() {
    setFormActionItems(prev => [...prev, { content: '', assignee_id: null }]);
  }

  function updateActionItem(index: number, field: keyof ActionItemDraft, value: string | null) {
    setFormActionItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  }

  function removeActionItem(index: number) {
    setFormActionItems(prev => prev.filter((_, i) => i !== index));
  }

  async function handleToggleCarriedForward(actionItemId: string) {
    if (!token) return;
    try {
      const updated = await retroService.toggleActionItem(actionItemId, token);
      if (updated.is_completed) {
        setCarriedForwardItems(prev => prev.filter(i => i.id !== actionItemId));
      }
    } catch (err) {
      console.error('Failed to toggle action item:', err);
    }
  }

  async function handleSave() {
    if (!token) return;
    try {
      setSaving(true);

      // Flatten section items
      const items: { id?: string; section_key: string; content: string; sort_order: number }[] = [];
      Object.entries(formSections).forEach(([sectionKey, sectionItems]) => {
        sectionItems.forEach((item, i) => {
          if (item.content.trim()) {
            items.push({ id: item.id, section_key: sectionKey, content: item.content.trim(), sort_order: i });
          }
        });
      });

      const actionItems = formActionItems
        .filter(ai => ai.content.trim())
        .map((ai, i) => ({
          id: ai.id,
          content: ai.content.trim(),
          assignee_id: ai.assignee_id || null,
          sort_order: i,
        }));

      if (editingRetro) {
        const updated = await retroService.updateRetrospective(editingRetro.id, {
          team_id: formTeamId || null,
          sprint_id: formSprintId || null,
          title: formTitle || null,
          status: formStatus,
          retro_date: formDate,
          items,
          attendee_ids: formAttendeeIds,
          action_items: actionItems,
        }, token);
        setRetros(prev => prev.map(r => r.id === updated.id ? updated : r));
        setSelectedId(updated.id);
        setSelectedRetro(updated);
      } else {
        const created = await retroService.createRetrospective({
          team_id: formTeamId || null,
          sprint_id: formSprintId || null,
          retro_type: formType,
          title: formTitle || null,
          status: formStatus,
          retro_date: formDate,
          items,
          attendee_ids: formAttendeeIds,
          action_items: actionItems,
        }, token);
        setRetros(prev => [created, ...prev]);
        setSelectedId(created.id);
        setSelectedRetro(created);
      }
      setShowForm(false);
    } catch (err) {
      console.error('Failed to save retrospective:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!token || !deleteId) return;
    try {
      setDeleting(true);
      await retroService.deleteRetrospective(deleteId, token);
      setRetros(prev => {
        const filtered = prev.filter(r => r.id !== deleteId);
        if (selectedId === deleteId) {
          setSelectedId(filtered.length > 0 ? filtered[0].id : null);
        }
        return filtered;
      });
      setDeleteId(null);
    } catch (err) {
      console.error('Failed to delete retrospective:', err);
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleDetailActionItem(actionItemId: string) {
    if (!token) return;
    try {
      await retroService.toggleActionItem(actionItemId, token);
      // Reload the selected retro
      if (selectedId) {
        const updated = await retroService.getRetrospective(selectedId, token);
        setSelectedRetro(updated);
      }
    } catch (err) {
      console.error('Failed to toggle action item:', err);
    }
  }

  // FORM VIEW
  if (showForm) {
    const typeConfig = RETRO_TYPES[formType];

    return (
      <Layout>
        <div className="p-6 space-y-4">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {editingRetro ? 'Edit Retrospective' : 'New Retrospective'}
                </h1>
                <p className="text-indigo-100 mt-1">
                  {typeConfig.label} retrospective
                </p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-white/20 text-white rounded-full font-medium hover:bg-white/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Section 1: Details — white card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Details</h2>
              <div className="flex items-center gap-2">
                {timerRemaining !== null && (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-mono font-bold ${
                    timerRemaining === 0
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 animate-pulse'
                      : timerRemaining <= 30
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                        : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                  }`}>
                    <span className="text-base">{timerRemaining === 0 ? "Time's up!" : formatTimer(timerRemaining)}</span>
                    {timerRemaining > 0 && (
                      <button
                        onClick={() => timerRunning ? stopTimer() : setTimerRunning(true)}
                        className="p-0.5 hover:opacity-70 transition-opacity"
                        title={timerRunning ? 'Pause' : 'Resume'}
                      >
                        {timerRunning ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        )}
                      </button>
                    )}
                    <button
                      onClick={resetTimer}
                      className="p-0.5 hover:opacity-70 transition-opacity"
                      title="Reset timer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowTimerModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 rounded-full shadow-sm transition-colors"
                  title="Set countdown timer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Timer
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder={`${typeConfig.label} Retrospective`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team</label>
                <select
                  value={formTeamId}
                  onChange={e => setFormTeamId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select team...</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sprint <span className="text-gray-400">(optional)</span>
                </label>
                <select
                  value={formSprintId}
                  onChange={e => setFormSprintId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">No sprint</option>
                  {sprints.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                <input
                  type="date"
                  value={formDate}
                  onChange={e => setFormDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
                <button
                  onClick={() => setFormStatus(formStatus === 'draft' ? 'completed' : 'draft')}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                    formStatus === 'completed'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                  }`}
                >
                  {formStatus === 'completed' ? 'Completed' : 'Draft'}
                </button>
              </div>
              {teamMembers.length > 0 && (
                <div className="flex items-center gap-2 ml-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">Attendees:</label>
                  <div className="flex flex-wrap gap-1.5">
                    {teamMembers.map(m => {
                      const checked = formAttendeeIds.includes(m.user_id);
                      return (
                        <button
                          key={m.user_id}
                          onClick={() => {
                            setFormAttendeeIds(prev =>
                              checked ? prev.filter(id => id !== m.user_id) : [...prev, m.user_id]
                            );
                          }}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            checked
                              ? 'bg-gradient-to-r from-indigo-600 to-blue-600 border-indigo-600 text-white shadow-sm'
                              : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
                          }`}
                        >
                          {m.first_name} {m.last_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Template sections — side by side columns */}
          <div className={`grid gap-4 ${
            typeConfig.sections.length === 2
              ? 'grid-cols-1 md:grid-cols-2'
              : typeConfig.sections.length === 3
                ? 'grid-cols-1 md:grid-cols-3'
                : typeConfig.sections.length === 4
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
                  : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'
          }`}>
            {typeConfig.sections.map(section => (
              <div key={section.key} className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 flex flex-col min-h-[250px]">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                    {section.label}
                  </h2>
                  {(formSections[section.key] || []).filter(i => i.content.trim()).length > 0 && (
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                      {(formSections[section.key] || []).filter(i => i.content.trim()).length}
                    </span>
                  )}
                </div>
                <div className="space-y-2 flex-1">
                  {(formSections[section.key] || []).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-1">
                      <input
                        type="text"
                        value={item.content}
                        onChange={e => updateSectionItem(section.key, idx, e.target.value)}
                        placeholder={`Add item...`}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={() => removeSectionItem(section.key, idx)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => addItemToSection(section.key)}
                  className="mt-2 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-left"
                >
                  + Add Item
                </button>
              </div>
            ))}
          </div>

          {/* Action Items section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                Action Items / Takeaways
              </h2>
              {(formActionItems.filter(a => a.content.trim()).length + carriedForwardItems.length) > 0 && (
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                  {formActionItems.filter(a => a.content.trim()).length + carriedForwardItems.length}
                </span>
              )}
            </div>

            {/* Carried-forward action items */}
            {carriedForwardItems.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2 uppercase tracking-wider">
                  Carried Forward
                </p>
                <div className="space-y-2">
                  {carriedForwardItems.map(item => (
                    <div key={item.id} className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => handleToggleCarriedForward(item.id)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{item.content}</span>
                      {item.assignee_name && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{item.assignee_name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {formActionItems.map((ai, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <input
                    type="text"
                    value={ai.content}
                    onChange={e => updateActionItem(idx, 'content', e.target.value)}
                    placeholder="Action item..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {teamMembers.length > 0 && (
                    <select
                      value={ai.assignee_id || ''}
                      onChange={e => updateActionItem(idx, 'assignee_id', e.target.value || null)}
                      className="w-40 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map(m => (
                        <option key={m.user_id} value={m.user_id}>{m.first_name} {m.last_name}</option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => removeActionItem(idx)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addActionItem}
              className="mt-2 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium"
            >
              + Add Action Item
            </button>
          </div>

          {/* Save footer */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {saving ? 'Saving...' : editingRetro ? 'Update Retrospective' : 'Create Retrospective'}
            </button>
          </div>
        </div>

        {/* Timer modal */}
        {showTimerModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowTimerModal(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                <h3 className="text-lg font-semibold text-white">Set Timer</h3>
                <p className="text-indigo-200 text-sm">Countdown timer for your retrospective</p>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="text-center">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Minutes</label>
                    <input
                      type="number"
                      min={0}
                      max={99}
                      value={timerMinutes}
                      onChange={e => setTimerMinutes(Math.max(0, Math.min(99, parseInt(e.target.value) || 0)))}
                      className="w-20 text-center text-3xl font-mono font-bold px-2 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <span className="text-3xl font-bold text-gray-400 mt-5">:</span>
                  <div className="text-center">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Seconds</label>
                    <input
                      type="number"
                      min={0}
                      max={59}
                      value={timerSeconds}
                      onChange={e => setTimerSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                      className="w-20 text-center text-3xl font-mono font-bold px-2 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  {[1, 3, 5, 10, 15].map(m => (
                    <button
                      key={m}
                      onClick={() => { setTimerMinutes(m); setTimerSeconds(0); }}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        timerMinutes === m && timerSeconds === 0
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 ring-1 ring-indigo-300 dark:ring-indigo-700'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {m}m
                    </button>
                  ))}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowTimerModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={startTimer}
                    disabled={timerMinutes === 0 && timerSeconds === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Start Timer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  // LIST + DETAIL VIEW
  return (
    <Layout>
      <div className="p-6">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Retrospectives</h1>
              <p className="text-indigo-100 mt-1">Track sprint reflections and carry forward action items</p>
            </div>
            <button
              onClick={() => setShowTypeModal(true)}
              className="px-4 py-2 bg-white text-indigo-600 rounded-full font-medium hover:bg-indigo-50 transition-colors"
            >
              New Retrospective
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : retros.length === 0 && !filterTeamId ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No retrospectives yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Create your first retrospective to start tracking team reflections.
            </p>
            <button
              onClick={() => setShowTypeModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
            >
              Create Retrospective
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex">
              {/* Left sidebar */}
              <div className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-700 p-4">
                {/* Team filter */}
                <div className="mb-3">
                  <select
                    value={filterTeamId}
                    onChange={e => { setFilterTeamId(e.target.value); setSelectedId(null); }}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">All Teams</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  {retros.map(retro => (
                    <button
                      key={retro.id}
                      onClick={() => setSelectedId(retro.id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm ${
                        selectedId === retro.id
                          ? 'text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/20'
                          : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">{formatDate(retro.retro_date)}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full ${
                          retro.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {retro.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {retro.title || RETRO_TYPE_LABELS[retro.retro_type] || retro.retro_type}
                        {retro.team_name && ` - ${retro.team_name}`}
                      </div>
                    </button>
                  ))}
                  {retros.length === 0 && filterTeamId && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">
                      No retrospectives for this team
                    </p>
                  )}
                </div>
              </div>

              {/* Right content area */}
              <div className="flex-1 min-w-0 p-6">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : selectedRetro ? (
                  <div>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {selectedRetro.title || RETRO_TYPE_LABELS[selectedRetro.retro_type] || selectedRetro.retro_type}
                        </h2>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span>{formatDate(selectedRetro.retro_date)}</span>
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            selectedRetro.status === 'completed'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          }`}>
                            {selectedRetro.status}
                          </span>
                          {selectedRetro.team_name && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                              {selectedRetro.team_name}
                            </span>
                          )}
                          {selectedRetro.sprint_name && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                              {selectedRetro.sprint_name}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          By {selectedRetro.created_by_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <button
                          onClick={() => openEditForm(selectedRetro)}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(selectedRetro.id)}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Attendees */}
                    {selectedRetro.attendees.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          Attendees
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedRetro.attendees.map(a => (
                            <span key={a.id} className="px-2.5 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {a.first_name} {a.last_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Section items */}
                    {(() => {
                      const typeConfig = RETRO_TYPES[selectedRetro.retro_type as RetroTypeKey];
                      if (!typeConfig) return null;
                      return (
                        <div className={`grid gap-4 mb-6 ${
                          typeConfig.sections.length === 2
                            ? 'grid-cols-1 md:grid-cols-2'
                            : typeConfig.sections.length === 3
                              ? 'grid-cols-1 md:grid-cols-3'
                              : typeConfig.sections.length === 4
                                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
                                : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'
                        }`}>
                          {typeConfig.sections.map(section => {
                            const sectionItems = selectedRetro.items.filter(i => i.section_key === section.key);
                            return (
                              <div key={section.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {section.label}
                                  </h3>
                                  {sectionItems.length > 0 && (
                                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                                      {sectionItems.length}
                                    </span>
                                  )}
                                </div>
                                {sectionItems.length > 0 ? (
                                  <ul className="space-y-2">
                                    {sectionItems.map(item => (
                                      <li key={item.id} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                        <span className="text-indigo-400 mt-1">&#8226;</span>
                                        <span>{item.content}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No items</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Action items */}
                    {selectedRetro.action_items.length > 0 && (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Action Items / Takeaways
                          </h3>
                          <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                            {selectedRetro.action_items.length}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {selectedRetro.action_items.map(ai => (
                            <div key={ai.id} className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={ai.is_completed}
                                onChange={() => handleToggleDetailActionItem(ai.id)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <span className={`text-sm flex-1 ${ai.is_completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                {ai.content}
                              </span>
                              {ai.assignee_name && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                                  {ai.assignee_name}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">Select a retrospective to view</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Type selection modal */}
        <RetroTypeModal
          isOpen={showTypeModal}
          onClose={() => setShowTypeModal(false)}
          onSelect={handleSelectType}
        />

        {/* Delete confirmation */}
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Retrospective</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this retrospective? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
